import React, { useState, useEffect } from 'react';
import { AlertTriangle, RotateCcw, X, Loader2, ShieldAlert } from 'lucide-react';
import { useResetCircuitBreaker } from '@/hooks/useDashboardQueries';
import { formatINR } from '@/lib/utils';
import { toast } from 'sonner';

export interface ResetCircuitBreakerModalProps {
  isOpen: boolean;
  onClose: () => void;
  dailyStats: {
    trades_today: number;
    realized_pnl: number;
    consecutive_losses: number;
  };
  limits: {
    max_trades_per_day: number;
    max_daily_loss: number;
    max_consecutive_losses: number;
  };
}

export const ResetCircuitBreakerModal: React.FC<ResetCircuitBreakerModalProps> = ({
  isOpen,
  onClose,
  dailyStats,
  limits,
}) => {
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetMutation = useResetCircuitBreaker();

  // Reset internal state when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setErrorMessage(null);
    }
  }, [isOpen]);

  // Keyboard navigation: Escape key closes modal when not pending
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !resetMutation.isPending) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, resetMutation.isPending, onClose]);

  if (!isOpen) return null;

  const isReasonValid = reason.trim().length > 0;
  const isSubmitting = resetMutation.isPending;

  const handleConfirm = () => {
    if (!isReasonValid || isSubmitting) return;

    setErrorMessage(null);
    resetMutation.mutate(
      { reason: reason.trim() },
      {
        onSuccess: (res) => {
          toast.success(res.message || 'Circuit breaker successfully reset');
          setReason('');
          onClose();
        },
        onError: (err) => {
          // Display the exact backend error message without replacing it with generic text
          setErrorMessage(err.message || 'Failed to reset circuit breaker');
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={() => !isSubmitting && onClose()}
      />

      {/* Modal Dialog */}
      <div
        className="relative z-50 w-full max-w-xl max-h-[90vh] overflow-y-auto bg-[var(--bg-surface)] border border-rose-900/60 rounded-xl shadow-2xl p-4 sm:p-6 space-y-4 sm:space-y-5 animate-fade-in-up font-mono my-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-modal-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1 pr-2">
            <h2 id="reset-modal-title" className="text-sm sm:text-base font-bold text-[var(--text-primary)] flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400 shrink-0" />
              <span>Circuit Breaker Reset</span>
            </h2>
            <p className="text-xs text-[var(--text-muted)]">
              Clear today&apos;s latched trading halt and restore new entry evaluation.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-50 transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning / Explanatory Alert */}
        <div className="p-3 rounded-lg bg-[var(--bg-surface-elevated)]/80 border border-[var(--border-default)] text-xs text-[var(--text-secondary)] space-y-1.5">
          <div className="flex items-center gap-2 text-amber-400 font-semibold text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Operational Latch Policy</span>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
            This action only clears the latched circuit-breaker state. It does not reset today&apos;s trade count, P&amp;L, or consecutive-loss history.
          </p>
        </div>

        {/* Status vs Limits Comparison Table */}
        <div className="border border-[var(--border-default)] rounded-lg overflow-hidden">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-[var(--bg-surface-elevated)]/90 text-[var(--text-muted)] border-b border-[var(--border-default)] text-[11px] uppercase">
                <th className="p-2.5">Metric</th>
                <th className="p-2.5 text-right">Current Daily Stat</th>
                <th className="p-2.5 text-right">Active Limit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-subtle)] text-[var(--text-secondary)]">
              <tr className="hover:bg-[var(--bg-surface-elevated)]/40">
                <td className="p-2.5 font-medium text-[var(--text-secondary)]">Trades Today</td>
                <td className="p-2.5 text-right font-bold tabular-nums text-[var(--text-primary)]">
                  {dailyStats.trades_today}
                </td>
                <td className="p-2.5 text-right tabular-nums text-[var(--text-muted)]">
                  {limits.max_trades_per_day} max
                </td>
              </tr>
              <tr className="hover:bg-[var(--bg-surface-elevated)]/40">
                <td className="p-2.5 font-medium text-[var(--text-secondary)]">Realized P&amp;L</td>
                <td
                  className={`p-2.5 text-right font-bold tabular-nums ${
                    dailyStats.realized_pnl < 0 ? 'text-rose-400' : 'text-emerald-400'
                  }`}
                >
                  {formatINR(dailyStats.realized_pnl, { showSign: true })}
                </td>
                <td className="p-2.5 text-right tabular-nums text-[var(--text-muted)]">
                  Max Loss: {formatINR(limits.max_daily_loss)}
                </td>
              </tr>
              <tr className="hover:bg-[var(--bg-surface-elevated)]/40">
                <td className="p-2.5 font-medium text-[var(--text-secondary)]">Consecutive Losses</td>
                <td className="p-2.5 text-right font-bold tabular-nums text-[var(--text-primary)]">
                  {dailyStats.consecutive_losses}
                </td>
                <td className="p-2.5 text-right tabular-nums text-[var(--text-muted)]">
                  {limits.max_consecutive_losses} max
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Backend Rejection / Failure Error Banner */}
        {errorMessage && (
          <div className="p-3 rounded-lg bg-rose-950/60 border border-rose-800/80 text-rose-300 text-xs flex items-start gap-2.5 animate-shake">
            <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="font-semibold text-rose-200">Reset Refused by Backend</p>
              <p className="text-[11px] text-rose-300/90 leading-relaxed break-words">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Required Reason Input */}
        <div className="space-y-1.5">
          <label htmlFor="circuit-breaker-reason" className="block text-xs font-semibold text-[var(--text-secondary)]">
            Reason for Reset <span className="text-rose-400">*</span>
          </label>
          <input
            id="circuit-breaker-reason"
            type="text"
            disabled={isSubmitting}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder="Why are you resetting today's circuit breaker?"
            className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-default)] rounded-md text-xs text-[var(--text-primary)] placeholder-[var(--text-dim)] focus:outline-none focus:ring-1 focus:ring-rose-500 disabled:opacity-50"
            autoFocus
          />
          <p className="text-[10px] text-[var(--text-muted)]">
            An operator audit reason is required and will be logged to SQLite audit history.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 pt-3 border-t border-[var(--border-default)]">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-4 py-2 rounded-md border border-[var(--border-strong)] bg-[var(--bg-surface-elevated)] hover:bg-[var(--bg-surface-muted)] text-[var(--text-secondary)] text-xs font-semibold transition-all disabled:opacity-50 cursor-pointer text-center"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isReasonValid || isSubmitting}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white text-xs font-semibold shadow-lg shadow-rose-950/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Resetting...</span>
              </>
            ) : (
              <>
                <RotateCcw className="w-4 h-4" />
                <span>Confirm Reset</span>
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
};
